import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, FolderPlus, Loader2, MessageSquare, Paperclip, Trash2, Users } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import EnTetePage from '../../components/layout/EnTetePage';
import MembresActivite from '../../components/assistants/MembresActivite';
import DemarrerEchange from '../../components/assistants/DemarrerEchange';
import {
  assistantsService,
  type Projet,
  type ProjetDetail,
  type DocumentActivite,
} from '../../services/api/assistants';

/**
 * Activités de travail : atelier, campagne, subvention.
 *
 * Une activité regroupe les échanges et les productions qui la concernent. On
 * y retrouve le budget, le TDR et le rapport d'un même atelier sans avoir à
 * les rechercher un par un.
 */
const ProjetsPage: React.FC = () => {
  const navigate = useNavigate();
  const [projets, setProjets] = useState<Projet[]>([]);
  const [detail, setDetail] = useState<ProjetDetail | null>(null);
  const [chargement, setChargement] = useState(true);
  const [nouveauNom, setNouveauNom] = useState('');
  const [creation, setCreation] = useState(false);
  const [documents, setDocuments] = useState<DocumentActivite[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreurDoc, setErreurDoc] = useState('');
  const [erreurAcces, setErreurAcces] = useState('');

  const recharger = () =>
    assistantsService.listerProjets().then(setProjets).finally(() => setChargement(false));

  useEffect(() => { recharger(); }, []);

  // Activité en cours d'ouverture : depuis Conakry, le détail met plusieurs
  // secondes, et l'écran restait sur « Sélectionnez une activité » comme si le
  // clic n'avait rien fait.
  const [ouverture, setOuverture] = useState<number | null>(null);

  const ouvrir = async (id: number) => {
    setDetail(null);
    setDocuments([]);
    setErreurDoc('');
    setErreurAcces('');
    setOuverture(id);
    try {
      // Le détail et les documents partent ensemble, plus l'un après l'autre.
      const [projet, pieces] = await Promise.all([
        assistantsService.chargerProjet(id),
        assistantsService.listerDocumentsProjet(id),
      ]);
      setDetail(projet);
      setDocuments(pieces);
    } catch (e: any) {
      setErreurAcces(e.message || "L'activité n'a pas pu être ouverte.");
    } finally {
      setOuverture(null);
    }
  };

  const televerser = async (fichier: File) => {
    if (!detail) return;
    setEnvoi(true);
    setErreurDoc('');
    try {
      const ajoute = await assistantsService.televerserDocumentProjet(detail.id, fichier);
      setDocuments((d) => [...d, ajoute]);
    } catch (e: any) {
      setErreurDoc(e.message || "Le fichier n'a pas pu être ajouté.");
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (documentId: number) => {
    if (!detail) return;
    await assistantsService.supprimerDocumentProjet(detail.id, documentId);
    setDocuments((d) => d.filter((x) => x.id !== documentId));
  };

  const creer = async () => {
    const nom = nouveauNom.trim();
    if (!nom) return;
    setCreation(true);
    try {
      const projet = await assistantsService.creerProjet(nom);
      setNouveauNom('');
      await recharger();
      await ouvrir(projet.id);
    } finally {
      setCreation(false);
    }
  };

  const dateLisible = (v: string) =>
    new Date(v).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  // Suppression en deux temps, sans fenêtre du navigateur.
  const [aSupprimer, setASupprimer] = useState(false);
  const [suppression, setSuppression] = useState(false);
  useEffect(() => { setASupprimer(false); }, [detail?.id]);

  const supprimerActivite = async () => {
    if (!detail) return;
    setSuppression(true);
    try {
      await assistantsService.supprimerProjet(detail.id);
      setDetail(null);
      setDocuments([]);
      await recharger();
    } catch (e: any) {
      setErreurAcces(e.message);
    } finally {
      setSuppression(false);
      setASupprimer(false);
    }
  };

  const carte = 'rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]';
  const titreSection = 'flex items-center gap-2 text-[13px] font-medium text-eg-accent';

  return (
    <DashboardLayout>
      <EnTetePage
        titre="Équipes et projets"
        description="Regroupez vos échanges, vos documents et vos productions par atelier, campagne ou subvention, et ouvrez-y l'accès à votre équipe."
      />
      <div className="mx-auto max-w-[1040px] space-y-6 pb-16 pt-8">

        <div className={`${carte} flex gap-3 p-3`}>
          <input
            value={nouveauNom}
            onChange={(e) => setNouveauNom(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && creer()}
            placeholder="Nouvelle activité, ex. : Atelier MLM Kankan, juin 2026"
            className="flex-1 rounded-xl bg-eg-papier px-3.5 py-2 text-[13.5px] text-eg-encre
              placeholder:text-eg-estompe focus:outline-none focus:ring-1 focus:ring-eg-accent"
          />
          <button
            type="button"
            onClick={creer}
            disabled={creation || !nouveauNom.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-eg-ombre px-4 py-2
              text-[13px] font-medium text-white hover:bg-eg-profond disabled:opacity-50"
          >
            {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
            Créer
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div>
            <h2 className="mb-3 text-[15px] font-medium text-eg-accent">Activités</h2>
            {chargement ? (
              <div className="flex items-center justify-center gap-2 py-12 text-[13px] text-eg-gris">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
              </div>
            ) : projets.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-8 text-center text-[12.5px] text-eg-estompe">
                Aucune activité. Créez-en une ci-dessus : vous pourrez y déposer vos documents,
                y ouvrir des échanges et en ouvrir l'accès à votre équipe.
              </p>
            ) : (
              <div className="space-y-2">
                {projets.map((p) => {
                  const choisi = detail?.id === p.id || ouverture === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => ouvrir(p.id)}
                      className={`w-full rounded-2xl px-4 py-3 text-left transition-colors ${
                        choisi
                          ? 'bg-eg-voile outline outline-1 -outline-offset-1 outline-eg-accent/40'
                          : `${carte} hover:bg-eg-papier`
                      }`}
                    >
                      <p className="text-[13.5px] font-medium text-eg-encre">{p.name}</p>
                      <p className="mt-0.5 font-mono text-[10.5px] text-eg-gris">
                        {p.nb_conversations} échange{p.nb_conversations > 1 ? 's' : ''}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 lg:pt-[34px]">
            {detail === null ? (
              <div className="rounded-2xl border border-dashed border-eg-bord py-20 text-center text-[13px] text-eg-estompe">
                {ouverture !== null ? (
                  <span className="inline-flex items-center gap-2 text-eg-gris">
                    <Loader2 className="h-4 w-4 animate-spin text-eg-accent" />
                    Ouverture de l'activité…
                  </span>
                ) : erreurAcces ? (
                  <span className="text-[#8A2A2A]">{erreurAcces}</span>
                ) : (
                  'Sélectionnez une activité pour voir son contenu.'
                )}
              </div>
            ) : (
              <div className={`${carte} space-y-6 p-6`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-[20px] font-medium text-eg-encre">{detail.name}</h2>
                    {detail.description && (
                      <p className="mt-1 text-[13px] text-eg-gris">{detail.description}</p>
                    )}
                  </div>
                  {detail.peut_supprimer && !aSupprimer && (
                    <button
                      type="button"
                      onClick={() => setASupprimer(true)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5
                        text-[12.5px] text-eg-gris hover:bg-[#FBF1F1] hover:text-[#8A2A2A]"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Supprimer
                    </button>
                  )}
                </div>

                {aSupprimer && (
                  <div className="rounded-xl bg-[#FBF1F1] px-4 py-3 text-[13px] text-[#8A2A2A]">
                    Supprimer « {detail.name} » ? Ses documents déposés et la liste de ses membres
                    disparaissent. Les échanges et les réunions sont conservés chez leurs auteurs.
                    <div className="mt-2.5 flex gap-2">
                      <button
                        type="button"
                        onClick={supprimerActivite}
                        disabled={suppression}
                        className="inline-flex items-center gap-2 rounded-full bg-[#8A2A2A] px-3.5 py-1.5
                          text-[12.5px] font-medium text-white disabled:opacity-50"
                      >
                        {suppression && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Supprimer l'activité
                      </button>
                      <button
                        type="button"
                        onClick={() => setASupprimer(false)}
                        className="rounded-full bg-eg-blanc px-3.5 py-1.5 text-[12.5px] text-eg-encre"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                <div className="border-b border-eg-bordure pb-6">
                  <h3 className={`mb-3 ${titreSection}`}>
                    <Users className="h-4 w-4" strokeWidth={1.8} /> Accès à l'activité
                  </h3>
                  {erreurAcces && (
                    <p className="mb-2 rounded-xl bg-eg-accent/10 px-3 py-2 text-[13px] text-eg-accent">
                      {erreurAcces}
                    </p>
                  )}
                  <MembresActivite
                    projetId={detail.id}
                    onErreur={(_titre, message) => setErreurAcces(message)}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <h3 className={titreSection}>
                      <Paperclip className="h-4 w-4" strokeWidth={1.8} /> Documents de l'activité
                    </h3>
                    <label className="cursor-pointer rounded-full px-2.5 py-1 text-[12.5px] font-medium
                      text-eg-accent hover:bg-eg-voile">
                      {envoi ? 'Envoi…' : '+ Ajouter'}
                      <input
                        type="file"
                        className="hidden"
                        disabled={envoi}
                        accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) televerser(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <p className="mt-1 text-[12px] text-eg-gris">
                    Micro-plan, liste des participants, TDR validé… Ces documents sont fournis
                    à tous les assistants ouverts depuis cette activité, et priment sur la
                    documentation générale.
                  </p>
                  {erreurDoc && <p className="mt-2 text-[13px] text-[#8A2A2A]">{erreurDoc}</p>}
                  {documents.length === 0 ? (
                    <p className="mt-2 text-[13px] text-eg-estompe">Aucun document.</p>
                  ) : (
                    <ul className="mt-2 overflow-hidden rounded-xl bg-eg-papier">
                      {documents.map((d, index) => (
                        <li
                          key={d.id}
                          className={`flex items-center justify-between px-3.5 py-2.5 text-[13px] ${
                            index > 0 ? 'border-t border-black/[0.05]' : ''
                          }`}
                        >
                          <span className="text-eg-encre">{d.name}</span>
                          <span className="flex items-center gap-3">
                            <span className="font-mono text-[10.5px] text-eg-estompe">
                              {d.caracteres.toLocaleString('fr-FR')} car.
                            </span>
                            <button
                              type="button"
                              onClick={() => supprimer(d.id)}
                              className="text-eg-estompe hover:text-[#8A2A2A]"
                              title="Retirer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <h3 className={titreSection}>
                    <MessageSquare className="h-4 w-4" strokeWidth={1.8} /> Échanges
                  </h3>

                  {/* On part du dossier et l'on choisit l'outil, plutôt que
                      d'ouvrir un assistant puis d'y rattacher l'activité. */}
                  <div className="mt-2">
                    <DemarrerEchange
                      projetId={detail.id}
                      onErreur={(_titre, message) => setErreurAcces(message)}
                    />
                    <p className="mt-1.5 text-[12px] text-eg-gris">
                      L'échange sera rattaché à cette activité et lira ses documents.
                    </p>
                  </div>

                  {detail.conversations.length === 0 ? (
                    <p className="mt-3 text-[13px] text-eg-estompe">
                      Aucun échange pour l'instant.
                    </p>
                  ) : (
                    <ul className="mt-2 divide-y divide-eg-bordure">
                      {detail.conversations.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => navigate(`/workspace/conversation/${c.id}`)}
                            className="w-full py-2 text-left text-[13px] hover:text-eg-accent"
                          >
                            <span className="font-medium text-eg-encre">{c.template_title}</span>
                            <span className="text-eg-gris"> · {c.auteur} · {dateLisible(c.updated_at)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <h3 className={titreSection}>
                    <FileSpreadsheet className="h-4 w-4" strokeWidth={1.8} /> Productions
                  </h3>
                  {detail.productions.length === 0 ? (
                    <p className="mt-2 text-[13px] text-eg-estompe">
                      Aucune production enregistrée pour l'instant.
                    </p>
                  ) : (
                    <ul className="mt-2 divide-y divide-eg-bordure">
                      {detail.productions.map((p) => (
                        <li key={p.message}>
                          <button
                            type="button"
                            onClick={() => navigate(`/workspace/conversation/${p.conversation}`)}
                            className="w-full py-2 text-left text-[13px] transition-colors hover:text-eg-accent"
                          >
                            <span className="font-medium text-eg-encre">{p.titre}</span>
                            <span className="text-eg-gris"> · {dateLisible(p.created_at)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProjetsPage;
